import { useEffect, useMemo, useState } from "react";
import WhatsAppApproval from "./WhatsAppApproval";
import { getModifiedDate, parseDateForSort, finalPdfLink, detectStatus, financeiroDisplay, duplicateRows, parseMoney as readMoney } from "./gestaoLogic";

const REQUIRED_CGU = [
  "CPF", "Nome", "Cargo", "Motivo", "Justificativa", "Origem", "Destino",
  "Data Ida", "Data Volta", "Total Diárias", "Valor Passagem",
  "Demais Despesas", "Valor Total Viagem",
];

const UPDATE_FIELDS = [
  "Valor Passagem",
  "Devolução",
  "Demais Despesas",
  "Valor Total Viagem",
  "Gestao Status Financeiro",
  "Gestao Data Pagamento",
  "Gestao Viagem Realizada",
  "Gestao Prestacao Contas",
  "Gestao Observacao",
];

const MONEY_FIELDS = new Set([
  "Valor Passagem",
  "Devolução",
  "Demais Despesas",
  "Valor Total Viagem",
]);

const STATUS_OPTIONS = ["ENVIADO", "APROVADO GERENTE", "APROVADO SUPERINTENDENTE", "Pendente", "Aguardando assinaturas", "Cancelada", "Rejeitada"];
const FINANCEIRO_OPTIONS = ["Pagamento pendente", "Paga", "Prestação pendente", "Pronta para CGU", "Cancelada", "Rejeitada"];

const CHART_COLORS = ["#0d5f8f", "#1f8f5f", "#8f6b0d", "#6a5acd", "#b54708", "#0a6570", "#9a3025", "#64748b"];

const S = {
  page: { minHeight: "100vh", background: "#eef4f8", color: "#16324f", fontFamily: "Inter, Segoe UI, Arial, sans-serif", padding: "22px 14px" },
  shell: { width: "min(1780px, calc(100vw - 28px))", margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", marginBottom: 18 },
  brand: { display: "flex", gap: 14, alignItems: "center" },
  logo: { width: 150, maxWidth: "32vw", objectFit: "contain", opacity: .9 },
  title: { margin: 0, fontSize: 26, color: "#0d3558", fontWeight: 850 },
  sub: { margin: "5px 0 0", color: "#5d7288", fontSize: 13, lineHeight: 1.45 },
  login: { maxWidth: 430, margin: "12vh auto", background: "#fff", border: "1px solid #d8e6ef", borderRadius: 10, padding: 24, boxShadow: "0 18px 45px rgba(16,50,80,.12)" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(185px,1fr))", gap: 12, marginBottom: 16 },
  card: { background: "#fff", border: "1px solid #d8e6ef", borderRadius: 8, padding: 14, minHeight: 82, boxShadow: "0 10px 28px rgba(16,50,80,.07)" },
  cardLabel: { color: "#647b92", fontSize: 11, textTransform: "uppercase", letterSpacing: .3, fontWeight: 800 },
  cardValue: { fontSize: 25, fontWeight: 900, color: "#10395e", marginTop: 8 },
  toolbar: { display: "grid", gridTemplateColumns: "minmax(210px,1fr) repeat(auto-fit,minmax(155px,1fr))", gap: 10, marginBottom: 14, alignItems: "center" },
  input: { width: "100%", boxSizing: "border-box", minHeight: 40, border: "1px solid #c7d8e7", borderRadius: 7, padding: "9px 11px", fontSize: 13, background: "#fff", color: "#173450" },
  btn: { minHeight: 40, border: 0, borderRadius: 7, padding: "9px 13px", fontWeight: 800, cursor: "pointer", background: "#0d5f8f", color: "#fff" },
  btnGhost: { minHeight: 40, border: "1px solid #bfd3e4", borderRadius: 7, padding: "9px 13px", fontWeight: 800, cursor: "pointer", background: "#fff", color: "#0d5f8f", whiteSpace: "nowrap" },
  btnDanger: { minHeight: 40, border: "1px solid #e7b2aa", borderRadius: 7, padding: "9px 13px", fontWeight: 800, cursor: "pointer", background: "#fff5f3", color: "#a83224" },
  tableWrap: { overflow: "auto", background: "#fff", border: "1px solid #d8e6ef", borderRadius: 8, boxShadow: "0 10px 28px rgba(16,50,80,.07)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1560 },
  th: { position: "sticky", top: 0, background: "#f6f9fb", color: "#587087", fontSize: 11, textTransform: "uppercase", textAlign: "left", padding: "11px 10px", borderBottom: "1px solid #d8e6ef", zIndex: 1 },
  td: { padding: "10px", borderBottom: "1px solid #eef3f7", fontSize: 12, verticalAlign: "top", color: "#1d3852" },
  tag: { display: "inline-flex", alignItems: "center", minHeight: 22, borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 850, whiteSpace: "nowrap" },
  panel: { position: "fixed", top: 0, right: 0, bottom: 0, width: "min(540px,96vw)", background: "#fff", borderLeft: "1px solid #d8e6ef", boxShadow: "-20px 0 45px rgba(16,50,80,.18)", zIndex: 50, display: "flex", flexDirection: "column" },
  panelHead: { padding: 18, borderBottom: "1px solid #e5eef5", display: "flex", justifyContent: "space-between", gap: 12 },
  panelBody: { padding: 18, overflow: "auto", display: "grid", gap: 12 },
  muted: { color: "#6b8197", fontSize: 12 },
  chartPanel: { display: "grid", gridTemplateColumns: "190px minmax(0,1fr)", gap: 18, alignItems: "center", background: "#fff", border: "1px solid #d8e6ef", borderRadius: 8, padding: 16, marginBottom: 16, boxShadow: "0 10px 28px rgba(16,50,80,.07)" },
  pie: { width: 168, height: 168, borderRadius: "50%", boxShadow: "inset 0 0 0 18px #fff, 0 10px 22px rgba(16,50,80,.12)" },
  legend: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 },
};

function value(row, key) {
  return String(row?.data?.[key] ?? "").trim();
}

function normalizeCpf(cpf) {
  const d = String(cpf || "").replace(/\D/g, "");
  if (d.length !== 11) return cpf || "";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatDateBR(v) {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split("-");
    return `${d}/${m}/${y}`;
  }
  return v;
}

function getYear(row) {
  const date = value(row, "Data Ida") || value(row, "Data Solicitação") || value(row, "Timestamp");
  const iso = String(date).match(/^(\d{4})-/);
  if (iso) return iso[1];
  const br = String(date).match(/(?:^|\D)(\d{2})\/(\d{2})\/(\d{4})(?:\D|$)/);
  return br ? br[3] : "";
}

function getQuarter(row) {
  const date = value(row, "Data Ida") || value(row, "Data Solicitação");
  if (!/^\d{4}-\d{2}/.test(date)) return "";
  return String(Math.ceil(Number(date.slice(5, 7)) / 3));
}

function parseMoney(v) { return readMoney(v) || 0; }

function normalizeMoneyInput(v) {
  if (!String(v || "").trim()) return "";
  return Number(parseMoney(v)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoneyBR(n) {
  return Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function isAereo(row) {
  return value(row, "Modalidade").toUpperCase().includes("AÉREO") || value(row, "Modalidade").toUpperCase().includes("AEREO");
}

function passagemDisplay(row) {
  if (!isAereo(row)) return "Não se aplica";
  const passagem = value(row, "Valor Passagem");
  return passagem ? `R$ ${normalizeMoneyInput(passagem)}` : "Pendente";
}

function isReadyForCgu(row) {
  const status = detectStatus(row);
  const complete = REQUIRED_CGU.every((field) => {
    if (field === "Valor Passagem" && !isAereo(row)) return true;
    return value(row, field);
  });
  return complete && status === "APROVADO SUPERINTENDENTE";
}

function statusStyle(status) {
  const map = {
    "Aguardando assinaturas": ["#fff3d8", "#895b00"],
    Aprovada: ["#e4f6e8", "#176331"],
    "Enviada ao financeiro": ["#e4f1ff", "#16558f"],
    "Pagamento pendente": ["#fff3d8", "#895b00"],
    Paga: ["#dff8ed", "#09613d"],
    "Prestação pendente": ["#fde8e5", "#9a3025"],
    "Pronta para CGU": ["#e7f8fa", "#0a6570"],
    Cancelada: ["#eceff3", "#5c6670"],
    Rejeitada: ["#fde8e5", "#9a3025"],
  };
  const [bg, color] = map[status] || ["#eceff3", "#5c6670"];
  return { ...S.tag, background: bg, color };
}

function uniq(items) {
  return [...new Set(items.map((item) => String(item || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function buildCsv(rows) {
  const headers = ["UF", "CPF", "NOME", "CARGO", "MOTIVO DA VIAGEM", "JUSTIFICATIVA DA VIAGEM", "ORIGEM", "DESTINO", "DATA DE IDA", "DATA DE VOLTA", "VALOR DIÁRIA", "VALOR DA PASSAGEM AÉREA", "DEVOLUÇÃO DE DIÁRIA", "DEMAIS DESPESAS", "VALOR TOTAL DA VIAGEM"];
  const lines = [headers];
  rows.filter(isReadyForCgu).forEach((row) => {
    const moeda = value(row, "Moeda") || "R$";
    lines.push([
      "RJ",
      normalizeCpf(value(row, "CPF")),
      value(row, "Nome"),
      value(row, "Cargo"),
      value(row, "Motivo"),
      value(row, "Justificativa"),
      value(row, "Origem"),
      value(row, "Destino"),
      formatDateBR(value(row, "Data Ida")),
      formatDateBR(value(row, "Data Volta")),
      `${moeda} ${value(row, "Total Diárias") || "0,00"}`,
      `R$ ${value(row, "Valor Passagem") || "0,00"}`,
      `R$ ${value(row, "Devolução") || "0,00"}`,
      `R$ ${value(row, "Demais Despesas") || "0,00"}`,
      `${moeda} ${value(row, "Valor Total Viagem") || "0,00"}`,
    ]);
  });
  return lines.map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
}

export default function Gestao() {
  const [auth, setAuth] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("gestaoAuth") || "null") || null;
    } catch {
      return null;
    }
  });
  const [loginEmail, setLoginEmail] = useState("pagamentos@rio.coop");
  const [loginPassword, setLoginPassword] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [quarter, setQuarter] = useState("");
  const [year, setYear] = useState("");
  const [setor, setSetor] = useState("");
  const [tipoViagem, setTipoViagem] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState({});

  const authed = Boolean(auth?.email && auth?.password);
  const authHeaders = () => ({
    "Content-Type": "application/json",
    "x-gestao-email": auth?.email || "",
    "x-gestao-password": auth?.password || "",
  });

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch("/api/gestao", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ action: "listGestao" }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload.result !== "success") throw new Error(payload.message || "Falha ao consultar a planilha.");
      setRows(payload.rows || []);
    } catch (err) {
      setError(err.message || "Falha ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const duplicates = useMemo(() => duplicateRows(rows), [rows]);

  const years = useMemo(() => uniq(rows.map(getYear)).reverse(), [rows]);
  const setores = useMemo(() => uniq(rows.map((row) => value(row, "Setor"))), [rows]);
  const tiposViagem = useMemo(() => uniq(rows.map((row) => value(row, "Tipo Diária"))), [rows]);
  const modalidades = useMemo(() => uniq(rows.map((row) => value(row, "Modalidade"))), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const result = rows.filter((row) => {
      if (status && detectStatus(row) !== status) return false;
      if (year && getYear(row) !== year) return false;
      if (quarter && getQuarter(row) !== quarter) return false;
      if (setor && value(row, "Setor") !== setor) return false;
      if (tipoViagem && value(row, "Tipo Diária") !== tipoViagem) return false;
      if (modalidade && value(row, "Modalidade") !== modalidade) return false;
      if (!q) return true;
      return ["Nome", "Cargo", "Setor", "Motivo", "Destino", "Origem", "Status", "Gestao Status Financeiro", "Tipo Diária", "Modalidade"].some((field) => value(row, field).toLowerCase().includes(q));
    });
    return result.sort((a, b) => {
      const diff = parseDateForSort(getModifiedDate(a)) - parseDateForSort(getModifiedDate(b));
      return sortDir === "asc" ? diff : -diff;
    });
  }, [rows, query, status, quarter, year, setor, tipoViagem, modalidade, sortDir]);

  const metrics = useMemo(() => {
    const total = filtered.length;
    const aguardando = filtered.filter((row) => ["ENVIADO", "APROVADO GERENTE", "Pendente", "Aguardando assinaturas"].includes(detectStatus(row))).length;
    const aprovadas = filtered.filter((row) => detectStatus(row) === "APROVADO SUPERINTENDENTE").length;
    const passagemPendente = filtered.filter((row) => isAereo(row) && !value(row, "Valor Passagem")).length;
    const prestacao = filtered.filter((row) => detectStatus(row) === "APROVADO SUPERINTENDENTE" && value(row, "Gestao Prestacao Contas") !== "Concluída").length;
    const cgu = filtered.filter(isReadyForCgu).length;
    const passagens = filtered.reduce((sum, row) => sum + parseMoney(value(row, "Valor Passagem")), 0);
    const diarias = filtered.reduce((sum, row) => sum + parseMoney(value(row, "Total Diárias")), 0);
    const modalidadesResumo = Object.entries(filtered.reduce((acc, row) => {
      const key = value(row, "Modalidade") || "Não informado";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})).sort((a, b) => b[1] - a[1]);
    const modalidades = modalidadesResumo.map(([nome, qtd], index) => ({
      nome,
      qtd,
      pct: total ? Math.round((qtd / total) * 100) : 0,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));
    return { total, aguardando, aprovadas, passagemPendente, prestacao, cgu, passagens, diarias, modalidades };
  }, [filtered]);

  const openRow = (row) => {
    setSelected(row);
    setEdit(UPDATE_FIELDS.reduce((acc, field) => ({ ...acc, [field]: field === "Gestao Link PDF Final" ? finalPdfLink(row) : value(row, field) }), {}));
  };

  const saveRow = async () => {
    if (!selected) return;
    setLoading(true);
    setError("");
    try {
      const valuesToSave = { ...edit };
      MONEY_FIELDS.forEach((field) => {
        valuesToSave[field] = normalizeMoneyInput(valuesToSave[field]);
      });
      const resp = await fetch("/api/gestao", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ action: "updateGestao", rowNumber: selected.rowNumber, values: valuesToSave }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload.result !== "success") throw new Error(payload.message || "Falha ao atualizar a planilha.");
      setSelected(null);
      await loadRows();
    } catch (err) {
      setError(err.message || "Falha ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = async () => {
    if (!selected) return;
    const ok = window.confirm(`Tem certeza que vai excluir este registro?\n\nLinha: ${selected.rowNumber}\nSolicitante: ${value(selected, "Nome") || "Sem nome"}\n\nEssa ação remove a linha da planilha.`);
    if (!ok) return;
    setLoading(true);
    setError("");
    try {
      const resp = await fetch("/api/gestao", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ action: "deleteGestao", rowNumber: selected.rowNumber }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload.result !== "success") throw new Error(payload.message || "Falha ao excluir o registro.");
      setSelected(null);
      await loadRows();
    } catch (err) {
      setError(err.message || "Falha ao excluir.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const csv = buildCsv(filtered);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `RJ_Rel_Viagens_${year || "todos"}_${quarter ? `${quarter}Trim` : "acumulado"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const login = () => {
    const nextAuth = { email: loginEmail.trim(), password: loginPassword };
    sessionStorage.setItem("gestaoAuth", JSON.stringify(nextAuth));
    setAuth(nextAuth);
  };

  const logout = () => {
    sessionStorage.removeItem("gestaoAuth");
    setAuth(null);
    setRows([]);
    setLoginPassword("");
  };

  if (!authed) {
    return (
      <div style={S.page}>
        <div style={S.login}>
          <img src="/logo_sescoop_app.png" alt="SESCOOP/RJ" style={{ ...S.logo, display: "block", marginBottom: 18 }} />
          <h1 style={S.title}>Gestão de Diárias</h1>
          <p style={S.sub}>Área restrita para acompanhamento financeiro, prestação de contas e relatório CGU.</p>
          <div style={{ height: 18 }} />
          <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
            <span style={S.cardLabel}>E-mail</span>
            <input style={S.input} type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={S.cardLabel}>Senha</span>
            <input style={S.input} type="password" placeholder="Senha de acesso" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") login(); }} />
          </label>
          <button style={{ ...S.btn, width: "100%", marginTop: 12 }} onClick={login}>Entrar</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.shell}>
        <header style={S.header}>
          <div style={S.brand}>
            <img src="/logo_sescoop_app.png" alt="SESCOOP/RJ" style={S.logo} />
            <div>
              <h1 style={S.title}>Gestão de Diárias</h1>
              <p style={S.sub}>CRM operacional para assinaturas, financeiro, prestação de contas e preparação do CSV CGU.</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.btnGhost} onClick={loadRows} disabled={loading}>{loading ? "Atualizando..." : "Atualizar"}</button>
            <button style={S.btnGhost} onClick={logout}>Sair</button>
          </div>
        </header>

        <section style={S.cardGrid}>
          <Metric label="Solicitações filtradas" value={metrics.total} />
          <Metric label="Aguardando assinatura" value={metrics.aguardando} />
          <Metric label="Aprovadas" value={metrics.aprovadas} />
          <Metric label="Passagem em branco" value={metrics.passagemPendente} />
          <Metric label="Prestação pendente" value={metrics.prestacao} />
          <Metric label="Prontas CGU" value={metrics.cgu} />
          <Metric label="Total pago com passagens" value={formatMoneyBR(metrics.passagens)} />
          <Metric label="Total pago de diárias" value={formatMoneyBR(metrics.diarias)} />
        </section>

        <ModalidadeChart data={metrics.modalidades} total={metrics.total} />

        <section style={S.toolbar}>
          <input style={S.input} placeholder="Buscar por nome, destino, motivo..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <select style={S.input} value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Todos os anos</option>
            {years.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select style={S.input} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos os status</option>
            {STATUS_OPTIONS.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select style={S.input} value={setor} onChange={(e) => setSetor(e.target.value)}>
            <option value="">Todos os setores</option>
            {setores.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select style={S.input} value={tipoViagem} onChange={(e) => setTipoViagem(e.target.value)}>
            <option value="">Todos os tipos</option>
            {tiposViagem.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select style={S.input} value={modalidade} onChange={(e) => setModalidade(e.target.value)}>
            <option value="">Todas as modalidades</option>
            {modalidades.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select style={S.input} value={quarter} onChange={(e) => setQuarter(e.target.value)}>
            <option value="">Ano acumulado</option>
            <option value="1">1º trimestre</option>
            <option value="2">2º trimestre</option>
            <option value="3">3º trimestre</option>
            <option value="4">4º trimestre</option>
          </select>
          <select style={S.input} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
            <option value="desc">Modificação: novo primeiro</option>
            <option value="asc">Modificação: antigo primeiro</option>
          </select>
          <button style={S.btnGhost} onClick={() => { setQuery(""); setStatus(""); setQuarter(""); setYear(""); setSetor(""); setTipoViagem(""); setModalidade(""); setSortDir("desc"); }}>Limpar filtros</button>
          <button style={S.btn} onClick={exportCsv}>Exportar CSV CGU</button>
        </section>

        {error && <div style={{ background: "#fde8e5", color: "#9a3025", padding: 12, borderRadius: 8, marginBottom: 12, fontWeight: 750 }}>{error}</div>}

        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Status", "Solicitante", "Setor", "Tipo", "Modalidade", "Destino", "Período", "Diárias", "Valor passagem", "Total", "Modificação", "Financeiro", "CGU", "Ação"].map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const st = detectStatus(row);
                return (
                  <tr key={row.rowNumber} style={duplicates.has(row.rowNumber) ? { background: "#fff3bf" } : undefined}>
                    <td style={S.td}><span style={statusStyle(st)}>{st}</span>{duplicates.has(row.rowNumber) && <div role="alert" style={{ color: "#6b4e00", fontWeight: 850, marginTop: 7, maxWidth: 230 }}>POSSÍVEL DUPLICIDADE — NÃO PAGAR SEM CONFIRMAR</div>}</td>
                    <td style={S.td}><strong>{value(row, "Nome") || "Sem nome"}</strong><br /><span style={S.muted}>{value(row, "Cargo")}</span></td>
                    <td style={S.td}>{value(row, "Setor") || "-"}</td>
                    <td style={S.td}>{value(row, "Tipo Diária") || "-"}</td>
                    <td style={S.td}>{value(row, "Modalidade") || "-"}</td>
                    <td style={S.td}>{value(row, "Origem")} → <strong>{value(row, "Destino")}</strong><br /><span style={S.muted}>{value(row, "Motivo")}</span></td>
                    <td style={S.td}>{formatDateBR(value(row, "Data Ida"))}<br />{formatDateBR(value(row, "Data Volta"))}</td>
                    <td style={S.td}>{value(row, "Moeda") || "R$"} {value(row, "Total Diárias") || "0,00"}</td>
                    <td style={S.td}>{passagemDisplay(row) === "Pendente" ? <span style={{ color: "#b54708", fontWeight: 850 }}>Pendente</span> : passagemDisplay(row)}</td>
                    <td style={S.td}><strong>{value(row, "Moeda") || "R$"} {value(row, "Valor Total Viagem") || "0,00"}</strong></td>
                    <td style={S.td}>{formatDateBR(getModifiedDate(row)) || "-"}</td>
                    <td style={S.td}>{financeiroDisplay(row)}<br /><span style={S.muted}>{value(row, "Gestao Data Pagamento")}</span></td>
                    <td style={S.td}>{isReadyForCgu(row) ? <span style={statusStyle("Pronta para CGU")}>Pronta</span> : <span style={statusStyle("Prestação pendente")}>Pendente</span>}</td>
                    <td style={{ ...S.td, minWidth: 96 }}><button style={{ ...S.btnGhost, width: "100%" }} onClick={() => openRow(row)}>Abrir</button></td>
                  </tr>
                );
              })}
              {!filtered.length && <tr><td style={S.td} colSpan={14}>{loading ? "Carregando..." : "Nenhuma solicitação encontrada."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <aside style={S.panel}>
          <div style={S.panelHead}>
            <div>
              <strong>{value(selected, "Nome")}</strong>
              <div style={S.muted}>{value(selected, "Destino")} · linha {selected.rowNumber}</div>
            </div>
            <button style={S.btnGhost} onClick={() => setSelected(null)}>Fechar</button>
          </div>
          <div style={S.panelBody}>
            {duplicates.has(selected.rowNumber) && <div role="alert" style={{ background: "#fff3bf", padding: 12, color: "#6b4e00", fontWeight: 850 }}>POSSÍVEL DUPLICIDADE — NÃO PAGAR SEM CONFIRMAR</div>}
            <WhatsAppApproval key={value(selected, "Protocolo")} protocolo={value(selected, "Protocolo")} email={auth.email} password={auth.password} />
            <div><strong>Gestão Link PDF Final</strong><br />{finalPdfLink(selected) ? <a href={finalPdfLink(selected)} target="_blank" rel="noreferrer">Abrir e baixar PDF final assinado</a> : <span style={S.muted}>PDF final indisponível</span>}</div>
            {UPDATE_FIELDS.map((field) => (
              <label key={field} style={{ display: "grid", gap: 5 }}>
                <span style={{ fontSize: 12, color: "#587087", fontWeight: 850 }}>{field}</span>
                {field === "Gestao Status Financeiro" ? (
                  <select style={S.input} value={edit[field] || ""} onChange={(e) => setEdit((v) => ({ ...v, [field]: e.target.value }))}>
                    <option value="">Selecione...</option>
                    {value(selected, "Gestao Status Financeiro") && !FINANCEIRO_OPTIONS.includes(value(selected, "Gestao Status Financeiro")) && <option>{value(selected, "Gestao Status Financeiro")}</option>}
                    {FINANCEIRO_OPTIONS.map((item) => <option key={item}>{item}</option>)}
                  </select>
                ) : field === "Gestao Viagem Realizada" || field === "Gestao Prestacao Contas" ? (
                  <select style={S.input} value={edit[field] || ""} onChange={(e) => setEdit((v) => ({ ...v, [field]: e.target.value }))}>
                    <option value="">Selecione...</option>
                    {field === "Gestao Viagem Realizada" ? <><option>Sim</option><option>Não</option></> : <><option>Pendente</option><option>Concluída</option><option>Não se aplica</option></>}
                  </select>
                ) : (
                  <input
                    style={S.input}
                    value={edit[field] || ""}
                    onChange={(e) => setEdit((v) => ({ ...v, [field]: e.target.value }))}
                    onBlur={() => {
                      if (MONEY_FIELDS.has(field)) setEdit((v) => ({ ...v, [field]: normalizeMoneyInput(v[field]) }));
                    }}
                  />
                )}
              </label>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              <button style={S.btn} onClick={saveRow} disabled={loading}>{loading ? "Salvando..." : "Salvar na planilha"}</button>
              <button style={S.btnDanger} onClick={deleteRow} disabled={loading}>Excluir registro</button>
              <button style={S.btnGhost} onClick={() => setSelected(null)}>Cancelar</button>
            </div>
            <div style={{ ...S.muted, lineHeight: 1.45 }}>Os campos de gestão são gravados em colunas auxiliares da planilha. A exclusão remove a linha da base após confirmação.</div>
          </div>
        </aside>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={S.card}>
      <div style={S.cardLabel}>{label}</div>
      <div style={S.cardValue}>{value}</div>
    </div>
  );
}

function ModalidadeChart({ data = [], total = 0 }) {
  if (!data.length) {
    return (
      <section style={S.chartPanel}>
        <div style={{ ...S.pie, background: "#e5eef5" }} />
        <div>
          <div style={S.cardLabel}>Viagens por modalidade</div>
          <div style={{ ...S.muted, marginTop: 8 }}>Nenhuma modalidade encontrada nos filtros atuais.</div>
        </div>
      </section>
    );
  }

  let cursor = 0;
  const gradient = data.map((item) => {
    const start = cursor;
    const end = cursor + (item.qtd / Math.max(total, 1)) * 100;
    cursor = end;
    return `${item.color} ${start}% ${end}%`;
  }).join(", ");

  return (
    <section style={S.chartPanel}>
      <div style={{ ...S.pie, background: `conic-gradient(${gradient})` }} title="Distribuição por modalidade" />
      <div>
        <div style={S.cardLabel}>Viagens por modalidade</div>
        <div style={{ ...S.legend, marginTop: 12 }}>
          {data.map((item) => (
            <div key={item.nome} style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: item.color, flex: "0 0 auto" }} />
              <span style={{ fontSize: 13, color: "#1d3852", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.nome}</span>
              <strong style={{ marginLeft: "auto", color: "#10395e", fontSize: 13 }}>{item.pct}%</strong>
              <span style={S.muted}>({item.qtd})</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
